import { useState, useEffect } from "react";
import { Oval } from "react-loader-spinner";
import { Eye, FileJson, Pencil, Plus, Trash2 } from "lucide-react";
import { observer } from "mobx-react-lite";
import type { SourceFile } from "./sources-types";
import { SourceNode, isFolder, isSourceEditable, isFile } from "./sources-types";
import { useStore } from "../../../../helpers";
import { useFolderContents } from "../../tars-hooks";
import type { SourceListItem } from "../../tars-hooks/useSourceList";
import { SourceNodeIcon, getSourceRowPaddingLeft } from "./SourceNodeIcon";
import DataRepoQuestionKeysDropdown from "./DataRepoQuestionKeysDropdown";
import { TARS_SOURCES_BUCKET } from "./add-source/addSourceUtils";
import { dataBankTreeSubtitle } from "../analytics/dataBankSourceHelpers";
const ACTION_SIZE = 15;

interface SourceTreeNodeProps {
  node: SourceNode;
  depth: number;
  notebookId: number | null;
  onAddSource: (parentId: string | null, parentLabel: string) => void;
  onEditSource: (node: SourceNode) => void;
  onDelete: (id: string, label: string) => void;
  onApplyDataRepoTemplate: (fileNode: SourceFile) => void;
}

const SourceTreeNode = observer(function SourceTreeNode({
  node,
  depth,
  notebookId,
  onAddSource,
  onEditSource,
  onDelete,
  onApplyDataRepoTemplate,
}: SourceTreeNodeProps) {
  const rootStore = useStore();
  const { tarsStore, tarsAnalyticsStore } = rootStore;
  const isFolderNode = isFolder(node);
  const [expanded, setExpanded] = useState(false);
  const { data: folderData, isLoading: isFolderLoading } = useFolderContents(
    notebookId,
    isFolderNode ? node.id : null,
    isFolderNode && expanded
  );

  useEffect(() => {
    if (!isFolderNode || !expanded || !folderData) return;
    const raw =
      folderData.sources ?? (Array.isArray(folderData) ? folderData : null);
    if (raw && Array.isArray(raw)) {
      tarsStore.setFolderChildren(node.id, raw as SourceListItem[]);
    }
  }, [isFolderNode, expanded, folderData, node.id, tarsStore]);

  const children = isFolderNode ? tarsStore.getChildren(node.id) : [];
  const paddingLeft = getSourceRowPaddingLeft(depth);

  const actionClass =
    "flex shrink-0 items-center justify-center rounded-md p-2 text-gray-400 transition-all duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#8DE038]/50";
  return (
    <li className="list-none">
      <div
        className={`group flex min-h-[36px] items-start gap-3 rounded-lg px-2.5 py-1.5 transition-[background-color,box-shadow] duration-150 hover:bg-white hover:shadow-sm ${
          (node.type === "dataRepo" || node.type === "dataBank") &&
          tarsAnalyticsStore.selectedSourceIds[0] === node.id
            ? "bg-white shadow-sm ring-1 ring-[#8DE038]/30"
            : ""
        }`}
        style={{ paddingLeft: `${paddingLeft}px` }}
      >
        <button
          type="button"
          className="mt-0.5 flex shrink-0 items-center border-0 bg-transparent p-0 outline-none focus:ring-2 focus:ring-[#8DE038]/40 focus:ring-offset-2 focus:ring-offset-[#F9FAFB] rounded-lg"
          onClick={() => isFolderNode && setExpanded((e) => !e)}
          aria-expanded={isFolderNode ? expanded : undefined}
          aria-label={isFolderNode ? "Expand or collapse folder" : undefined}
        >
          <SourceNodeIcon
            node={node}
            expanded={expanded}
            isFolderNode={isFolderNode}
          />
        </button>
        <div
          className="min-w-0 flex-1"
          role={node.type === "dataRepo" || node.type === "dataBank" ? "button" : undefined}
          tabIndex={node.type === "dataRepo" || node.type === "dataBank" ? 0 : undefined}
          onClick={() => {
            if (node.type === "dataRepo" || node.type === "dataBank") {
              tarsAnalyticsStore.setSelectedSourceIds([node.id]);
            }
          }}
          onKeyDown={(e) => {
            if (
              (node.type === "dataRepo" || node.type === "dataBank") &&
              (e.key === "Enter" || e.key === " ")
            ) {
              e.preventDefault();
              tarsAnalyticsStore.setSelectedSourceIds([node.id]);
            }
          }}
        >
          <span className="block truncate text-[13px] font-medium text-gray-900">
            {node.name}
            {isFolderNode && expanded && isFolderLoading && (
              <span className="ml-2 inline-flex align-middle">
                <Oval
                  height={12}
                  width={12}
                  color="#9ca3af"
                  ariaLabel="Loading"
                  visible
                  wrapperStyle={{ display: "inline-block" }}
                  wrapperClass=""
                />
              </span>
            )}
          </span>
          {node.type === "dataRepo" && (node as SourceFile).es_index && (
            <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
              <span className="min-w-0 flex-1 truncate text-[11px] font-normal text-gray-500">
                {(node as SourceFile).es_index}
              </span>
              <DataRepoQuestionKeysDropdown
                active={
                  tarsAnalyticsStore.selectedSourceIds[0] === node.id &&
                  !!(node as SourceFile).es_index
                }
                keys={tarsAnalyticsStore.projectQuestionKeys}
                loading={tarsAnalyticsStore.projectQuestionKeysLoading}
                fetchError={tarsAnalyticsStore.projectQuestionKeysError}
              />
            </div>
          )}
          {node.type === "dataBank" && (
            <div className="mt-0.5 truncate text-[11px] font-normal text-gray-500">
              {dataBankTreeSubtitle(node as SourceFile)}
            </div>
          )}
        </div>
        <span className="mt-0.5 flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity duration-150 group-hover:opacity-100">
          {isFolderNode && (
            <button
              type="button"
              className={actionClass}
              onClick={(e) => {
                e.stopPropagation();
                onAddSource(node.id, node.name);
              }}
              aria-label="Add source"
            >
              <Plus size={ACTION_SIZE} strokeWidth={2} />
            </button>
          )}
          {isFile(node) && ((node as SourceFile).url != null || (node as SourceFile).s3_key != null) && (
            <button
              type="button"
              className={actionClass}
              onClick={async (e) => {
                e.stopPropagation();
                const fileNode = node as SourceFile;
                let url = fileNode.url;
                if (!url && fileNode.s3_key) {
                  url = (await rootStore.get_s3_url(TARS_SOURCES_BUCKET, fileNode.s3_key)) ?? undefined;
                }
                if (url) window.open(url, "_blank", "noopener,noreferrer");
              }}
              aria-label="Preview in new tab"
            >
              <Eye size={ACTION_SIZE} strokeWidth={1.8} />
            </button>
          )}
          {isSourceEditable(node) && (
            <button
              type="button"
              className={actionClass}
              onClick={(e) => {
                e.stopPropagation();
                onEditSource(node);
              }}
              aria-label="Edit"
            >
              <Pencil size={ACTION_SIZE} strokeWidth={1.8} />
            </button>
          )}
          {node.type === "dataRepo" && (
            <button
              type="button"
              className={actionClass}
              onClick={(e) => {
                e.stopPropagation();
                onApplyDataRepoTemplate(node as SourceFile);
              }}
              aria-label="Create facet from template"
              title="Create facet JSON template"
            >
              <FileJson size={ACTION_SIZE} strokeWidth={1.8} />
            </button>
          )}
          <button
            type="button"
            className={`${actionClass} hover:text-red-600 hover:bg-red-50`}
            onClick={(e) => {
              e.stopPropagation();
              onDelete(node.id, node.name);
            }}
            aria-label="Delete"
          >
            <Trash2 size={ACTION_SIZE} strokeWidth={1.8} />
          </button>
        </span>
      </div>
      {isFolderNode && expanded && children.length > 0 && (
        <ul
          className="relative mt-px space-y-px border-l border-gray-100 pl-2 ml-5"
          aria-label={`Contents of ${node.name}`}
        >
          {children.map((child) => (
            <SourceTreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              notebookId={notebookId}
              onAddSource={onAddSource}
              onEditSource={onEditSource}
              onDelete={onDelete}
              onApplyDataRepoTemplate={onApplyDataRepoTemplate}
            />
          ))}
        </ul>
      )}
    </li>
  );
});

export default SourceTreeNode;
